import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Briefcase, Search, MoreHorizontal, Calendar, MapPin, Shield } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { AssignProjectModal } from './AssignProjectModal';
import { useTeamProjects } from '../hooks/useTeamProjects';
import type { TeamDetailData } from '../types/teamsDetail.types';

interface ProjectManagementBlockProps {
  projects: TeamDetailData['projects'];
}

export function ProjectManagementBlock({ projects }: ProjectManagementBlockProps) {
  const { id } = useParams();
  const [search, setSearch] = useState('');
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  
  const { availableProjects, isLoadingProjects, isMutating, assignProjects } = useTeamProjects(id || '');

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.zone.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white rounded-md border border-slate-100 overflow-hidden">
      <div className="p-4 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
            <Briefcase className="w-5 h-5" />
          </div>
          <div>
            <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Danh sách dự án phụ trách</h3>
            <p className="sm:text-sm text-xs text-slate-400">Quản lý các công trình, dự án mà tổ đội đang tham gia</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Tìm tên dự án, khu vực..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button 
            onClick={() => setIsAssignModalOpen(true)}
            className="bg-emerald-800 hover:bg-emerald-900 text-white sm:text-sm text-xs h-9 px-4 shrink-0 border-none"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Giao dự án
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50">
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Dự án</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Vai trò</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Khu vực</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Ngày bắt đầu</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Trạng thái</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredProjects.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-400 sm:text-sm text-xs italic">
                  Không tìm thấy dự án phù hợp.
                </td>
              </tr>
            ) : (
              filteredProjects.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/30 transition-colors group">
                  <td className="px-6 py-4">
                    <p className="sm:text-sm text-xs text-slate-900 font-normal leading-tight">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-400">ID: PRJ-{p.id.padStart(4, '0')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="sm:text-sm text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md font-normal flex items-center w-max gap-1">
                      <Shield className="w-3 h-3" /> {p.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      {p.zone}
                    </div>
                  </td>
                  <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {p.start_date}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge label={p.status_label} variant={p.status_variant} className="sm:text-sm text-xs px-2 py-0.5" />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AssignProjectModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        availableProjects={availableProjects}
        isLoading={isLoadingProjects}
        isMutating={isMutating}
        onAssign={(projectIds) => assignProjects(projectIds, () => {
          setIsAssignModalOpen(false);
          // refresh logic could be triggered here if connected to real data
        })}
      />
    </div>
  );
}
